import { wsService } from './wsService.ts';

/**
 * Relay for local (localhost) MCP servers. The Node.js server cannot reach a
 * user's localhost MCP server, so it forwards MCP JSON-RPC calls over the chat
 * WebSocket; this service executes them in the browser (where localhost is the
 * user's own machine) and sends the result back.
 *
 * The MCP SDK is loaded lazily (dynamic import) the first time a relay request
 * arrives, so it stays out of the main bundle until a local MCP server is used.
 */

type RelayServerConfig = {
  url: string;
  type: 'sse' | 'http';
  authentication: 'none' | 'token' | 'oauth2.1';
  token?: string;
};

type RelayRequest = {
  requestId: string;
  action: 'listTools' | 'callTool';
  server: RelayServerConfig;
  toolName?: string;
  toolArguments?: Record<string, unknown>;
};

type SdkModules = Awaited<ReturnType<typeof McpRelayService._loadSdk>>;

class McpRelayService {
  private _sdk: SdkModules | null = null;
  private _loading: Promise<SdkModules> | null = null;

  constructor() {
    wsService.on('mcp:relay_request', (data) => {
      void this._handle(data as RelayRequest);
    });
  }

  static async _loadSdk() {
    const [{ Client }, sse, http] = await Promise.all([
      import('@modelcontextprotocol/sdk/client/index.js'),
      import('@modelcontextprotocol/sdk/client/sse.js'),
      import('@modelcontextprotocol/sdk/client/streamableHttp.js'),
    ]);
    return { Client, SSEClientTransport: sse.SSEClientTransport, StreamableHTTPClientTransport: http.StreamableHTTPClientTransport };
  }

  private async _getSdk(): Promise<SdkModules> {
    if (this._sdk) return this._sdk;
    if (!this._loading) {
      this._loading = McpRelayService._loadSdk().then((sdk) => {
        this._sdk = sdk;
        return sdk;
      });
    }
    return await this._loading;
  }

  private async _connect(sdk: SdkModules, server: RelayServerConfig) {
    const mcpClient = new sdk.Client(
      { name: 'ollama-browser-mcp-client', version: '1.0.0' },
      { capabilities: {} }
    );

    const fetchWithAuth = server.token
      ? (url: string | URL | Request, init?: RequestInit) => {
          const headers = new Headers((init?.headers as Headers) ?? init?.headers ?? {});
          headers.set('Authorization', `Bearer ${server.token}`);
          return fetch(url as string, { ...init, headers });
        }
      : undefined;

    let transport;
    if (server.type === 'sse') {
      transport = new sdk.SSEClientTransport(new URL(server.url), fetchWithAuth ? { eventSourceInit: { fetch: fetchWithAuth } } : {});
    } else {
      transport = new sdk.StreamableHTTPClientTransport(new URL(server.url), fetchWithAuth ? { requestInit: { headers: { Authorization: `Bearer ${server.token}` } } } : {});
    }

    await mcpClient.connect(transport);
    return mcpClient;
  }

  private async _handle(request: RelayRequest) {
    if (!request?.requestId) return;
    try {
      if (!request.server?.url) {
        throw new Error('Relay request is missing a server URL');
      }

      const sdk = await this._getSdk();
      const mcpClient = await this._connect(sdk, request.server);
      try {
        if (request.action === 'listTools') {
          const { tools } = await mcpClient.listTools();
          wsService.send('mcp:relay_response', {
            requestId: request.requestId,
            ok: true,
            result: {
              tools: tools.map((t) => ({
                name: t.name,
                title: t.title,
                description: t.description,
                inputSchema: t.inputSchema,
                outputSchema: t.outputSchema,
                annotations: t.annotations,
              })),
            },
          });
        } else if (request.action === 'callTool') {
          if (!request.toolName) throw new Error('callTool relay request is missing a tool name');
          const callResult = await mcpClient.callTool({ name: request.toolName, arguments: (request.toolArguments ?? {}) as Record<string, unknown> });
          wsService.send('mcp:relay_response', {
            requestId: request.requestId,
            ok: true,
            result: { content: callResult.content, isError: callResult.isError },
          });
        } else {
          throw new Error(`Unknown MCP relay action: ${String((request as RelayRequest).action)}`);
        }
      } finally {
        await mcpClient.close();
      }
    } catch (err) {
      wsService.send('mcp:relay_response', {
        requestId: request.requestId,
        ok: false,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
}

export const mcpRelayService = new McpRelayService();
